import {Component, computed, DestroyRef, effect, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {combineLatest, map} from 'rxjs';
import {AsyncPipe} from '@angular/common';
import {DomSanitizer} from '@angular/platform-browser';
import {PdfExportService} from '@/app/shared/services/pdf-export.service';

import {CampaignStoreService, CardResumen,} from '../../../campaña/services/campaign-store.service';
import {ReportControlsComponent, Scenario,} from '../../components/report-controls/report-controls.component';
import {AnalysisMode} from '../../components/filters-report/reporte-filtros.component';

import {ReportesTitleComponent} from '../../components/header-report/reportes-title.component';
import {CardCampanaComponent} from '../../../campaña/components/card-campana-component/card-campana-component';
import {InfoCardComponent} from '@/app/shared/components/info-card/info-card.component';
import {DonutCardComponent} from '@/app/shared/components/donut-card/donut-card.component';
import {EmptyStateComponent} from '@/app/shared/components/empty-state/empty-state.component';
import {AppIcons} from '@/assets/icons/icons';
import {ReportFiltersStateService} from '../../components/filters-report/service/report-filters-state.service';
import {ReportScenarioComponent} from '@/app/features/reportes/components/report-scenario/report-scenario.component';
import {ThemeService} from '@/app/core/services/theme/theme.service';

@Component({
  selector: 'app-guia1-report',
  standalone: true,
  templateUrl: './guia1-report.html',
  styleUrl: './guia1-report.scss',
  imports: [
    AsyncPipe,
    ReportesTitleComponent,
    CardCampanaComponent,
    InfoCardComponent,
    DonutCardComponent,
    EmptyStateComponent,
    ReportControlsComponent,
    ReportScenarioComponent,
  ],
})
export class Guia1ReportComponent implements OnInit {
  private themeService = inject(ThemeService);
  protected atsDonutColors = computed(() => {
    const colors = this.themeColors();
    return {
      primary: colors?.colorResultVeryHigh ?? '#000',
      secondary: colors?.colorChartNeutral ?? '#ccc',
    };
  });
  private store: CampaignStoreService = inject(CampaignStoreService);
  razonOrg$ = combineLatest([
    this.store.companyName$,
    this.store.totalEmployees$,
    this.store.campaignMeta$,
  ]).pipe(
    map(([companyName, totalEmployees, meta]) => ({
      companyName,
      evaluation: this.corporateContent()?.evaluationContent ?? '',
      methodology: this.corporateContent()?.metodologyContent ?? '',
      totalEmployees,
      start: meta.startDateLabel,
      end: meta.endDateLabel,
      durationDays: meta.durationDays,
    }))
  );
  private destroyRef = inject(DestroyRef);
  private reportFiltersStateService = inject(ReportFiltersStateService);

  protected themeContent = this.themeService.themeContent;
  protected themeColors = computed(() => this.themeService.getTheme()?.colors);

  protected reportContent = computed(() => this.themeContent()?.reports);
  protected severeTraumaticEventContent = computed(() => this.reportContent()?.severeTraumaticEventContent);
  protected corporateContent = computed(() => this.reportContent()?.corporateCompanyNameContent);
  protected nextStepsContent = computed(() => this.reportContent()?.nextStepsContent);

  // Contenido de texto para las donas
  protected evaluatedDonutContent = computed(() => {
    const donutContent = this.reportContent()?.donutCardComponent;
    return {
      title: donutContent?.title ?? '',
      primaryLabel: donutContent?.evaluatedLabel ?? '',
      secondaryLabel: donutContent?.notEvaluatedLabel ?? '',
    };
  });

  protected atsDonutContent = computed(() => {
    const donutContent = this.reportContent()?.donutCardComponent;
    return {
      title: donutContent?.severeTraumaticEventTitle ?? '',
      primaryLabel: donutContent?.severeTraumaticEventLabel ?? '',
      secondaryLabel: donutContent?.noSevereTraumaticEventLabel ?? '',
    };
  });

  // Colores para las donas
  protected evaluatedDonutColors = computed(() => {
    const colors = this.themeColors();
    return {
      primary: colors?.colorChartSuccess ?? '#000',
      secondary: colors?.colorChartNeutral ?? '#ccc',
    };
  });
  private pdfExportService = inject(PdfExportService);

  protected pageData = signal<{ title: string; subTitle: string; }>({title: '', subTitle: ''});

  protected analysisMode: AnalysisMode = 'final-assessments';
  protected hasAppliedFilters = false;
  protected scenarios: Scenario[] = [];
  protected isExporting = false;

  totalEmployees$ = this.store.totalEmployees$;
  evaluados$ = this.store.respondedCount$;
  conAts$ = this.store.conAtsCount$;
  respondedCount$ = this.store.respondedCount$;
  private sanitizer = inject(DomSanitizer);

  atsIcon = this.sanitizer.bypassSecurityTrustHtml(AppIcons.ATS);
  rzoIcon = this.sanitizer.bypassSecurityTrustHtml(AppIcons.RZO);
  spIcon = this.sanitizer.bypassSecurityTrustHtml(AppIcons.SP);

  leftCards$ = combineLatest([
    this.store.summaryCards$,
    this.store.percentageCard$,
  ]).pipe(
    map(([cols, pc]) => {
      const activada = cols[0]?.[0] ?? null;
      const diasFin = cols[1]?.[1] ?? null;

      const diasFinGray = diasFin ? { ...diasFin, colorIcon: 'gray' } : null;

      const cumplimientoMini: CardResumen | null = pc
        ? {
            titulo: `${pc.percentage}%`,
            subtitulo: 'Nivel de cumplimiento',
            icon: this.sanitizer.bypassSecurityTrustHtml(AppIcons.CIRCLE),
            colorIcon: pc.colorIcon,
          }
        : null;

      return [activada, cumplimientoMini, diasFinGray].filter(
        Boolean
      ) as CardResumen[];
    })
  );

  private readonly selectedCampaignId$ = this.store.selectedCampaignClient$.pipe(
    map((c) => c?.publicId ?? null)
  );
  protected readonly campaignId = toSignal(this.selectedCampaignId$, {
    initialValue: null,
  });
  protected readonly respondedCountSignal = toSignal(this.store.respondedCount$, {
    initialValue: 0,
  });

  constructor() {
    this.reportFiltersStateService.clearState();
    effect(() => {
      const title = this.themeContent()?.reports?.guideOneReportComponent?.title ?? '';
      this.pageData.set({title: title, subTitle: ''});
    });
  }

  ngOnInit() {
    this.store.selectedCampaign$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.scenarios = [];
        this.hasAppliedFilters = false;
        this.analysisMode = 'final-assessments';
      });
  }

  removeScenario(id: string) {
    this.scenarios = this.scenarios.filter((s) => s.id !== id);
    if (this.scenarios.length === 0) {
      this.hasAppliedFilters = false;
      this.analysisMode = 'final-assessments';
    }
  }

  exportToPdf() {
    this.isExporting = true;
    // Esperar un poco más para que los gráficos se rendericen completamente
    setTimeout(() => {
      this.pdfExportService.exportToPdf('.pdf-export', 'reporte-guia1.pdf').then(() => {
        this.isExporting = false;
      });
    }, 2000);
  }
}
